import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { SignInMethods } from "@/components/auth/SignInMethods";
import { PhoneAuth } from "@/components/auth/PhoneAuth";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Download, FileDown, LogOut, Trash2, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useApi, errMsg, fmtDate, downloadFile } from "@/lib/api";
import { useApp } from "@/lib/app-context";
import { CURRENCIES, TIMEZONES, countryName, currencyFor } from "@/lib/geo";
import { CountrySelect } from "@/components/ww/Misc";
import { PricingCards } from "@/components/ww/Pricing";
import { FamilySharing } from "@/components/ww/FamilySharing";

const Panel = ({ title, children, testid }) => (
  <section className="rounded-[26px] border border-ww-border bg-white p-6 sm:p-8" data-testid={testid}>
    <h2 className="mb-6 font-display text-xl font-semibold text-ww-navy">{title}</h2>{children}
  </section>
);
const Verified = ({ ok, testid }) => ok
  ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-ww-emerald" data-testid={testid}><CheckCircle2 className="h-3.5 w-3.5" />Verified</span>
  : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid={testid}><AlertCircle className="h-3.5 w-3.5" />Not verified</span>;
const Row = ({ label, children }) => (
  <div className="flex flex-col gap-1 border-b border-ww-border/70 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between"><span className="text-sm text-ww-muted">{label}</span><div className="text-sm font-semibold text-ww-navy">{children}</div></div>
);

function Profile() {
  const { me, setMe } = useApp();
  const api = useApi();
  const [methods, setMethods] = useState(false);
  const [name, setName] = useState(me.name || "");
  const save = async (patch) => {
    try { setMe(await api("patch", "/me", patch)); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const manage = () => setMethods(true);
  const resync = async () => { try { setMe(await api("post", "/me/sync")); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Profile" testid="account-profile-panel">
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <input className="ww-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} placeholder="Full Name" data-testid="profile-name-input" />
        <button className="ww-btn-primary justify-center" onClick={() => save({ name })} data-testid="profile-name-save">Save name</button>
      </div>
      <div className="mt-4">
        <Row label="Email"><span className="mr-2" data-testid="profile-email">{me.email || "Not added"}</span>{me.email && <Verified ok={me.email_verified} testid="profile-email-status" />}</Row>
        <Row label="Mobile Number"><span className="mr-2">{me.phone || "Not added"}</span>{me.phone ? <Verified ok={me.phone_verified} testid="profile-phone-status" /> : <button onClick={manage} className="text-xs font-semibold text-ww-emerald hover:underline" data-testid="profile-add-phone-button">Add mobile number</button>}</Row>
        <Row label="Connected Sign-in Methods"><span className="capitalize" data-testid="profile-providers">{(me.providers || []).join(", ") || "—"}</span></Row>
        <Row label="Country"><CountrySelect value={me.country} onChange={(cc) => save({ country: cc, currency: currencyFor(cc) })} testid="profile-country-select" className="w-56" /></Row>
        <Row label="Preferred Currency">
          <Select value={me.currency || undefined} onValueChange={(v) => save({ currency: v })}><SelectTrigger className="h-11 w-56 rounded-xl" data-testid="profile-currency-select"><SelectValue placeholder="Currency" /></SelectTrigger><SelectContent className="max-h-64">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </Row>
        <Row label="Preferred Timezone">
          <Select value={me.timezone} onValueChange={(v) => save({ timezone: v })}><SelectTrigger className="h-11 w-56 rounded-xl" data-testid="profile-timezone-select"><SelectValue /></SelectTrigger><SelectContent className="max-h-64">{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        </Row>
      </div>
      <button className="ww-btn-ghost mt-6" onClick={manage} data-testid="profile-manage-signin-button">Manage email, mobile & sign-in methods</button>
      <SignInMethods open={methods} onOpenChange={setMethods} onChanged={resync} />
      <p className="mt-2 text-xs text-ww-muted">New emails and mobile numbers are verified with a one-time code before they take effect.</p>
    </Panel>
  );
}

function Subscription() {
  const { me } = useApp();
  const api = useApi();
  const [sub, setSub] = useState(null);
  const [manage, setManage] = useState(false);
  useEffect(() => { api("get", "/billing/subscription").then(setSub).catch(() => {}); }, [api, me.plan]);
  const cur = sub?.current;
  return (
    <Panel title="Subscription" testid="account-subscription-panel">
      <Row label="Current Plan"><span data-testid="sub-current-plan">{me.plan_name}</span></Row>
      <Row label="Product Limit">{me.limit} records</Row>
      <Row label="Products Used"><span data-testid="sub-products-used">{me.used}</span></Row>
      <Row label="Annual Price">{cur ? cur.amount_display : "Free"}</Row>
      <Row label="Billing Currency">{cur?.currency || "—"}</Row>
      <Row label="Start Date">{cur ? fmtDate(cur.start_date) : fmtDate(me.created_at?.slice(0, 10))}</Row>
      <Row label="Expiry Date">{cur ? fmtDate(cur.end_date) : "Never"}</Row>
      <Row label="Renewal Status">{cur ? "Manual renewal (no automatic charges)" : "Not applicable"}</Row>
      <button className="ww-btn-primary mt-6" onClick={() => setManage(true)} data-testid="account-manage-plan-button">Manage Plan</button>
      {sub?.history?.length > 0 && (
        <div className="mt-8"><p className="mb-2 text-sm font-semibold text-ww-navy">Subscription history</p>
          {sub.history.map((h) => <p key={h.id} className="text-sm text-ww-muted">{h.plan} · {fmtDate(h.start_date)} – {fmtDate(h.end_date)} · <span className="capitalize">{h.status}</span></p>)}</div>
      )}
      <Dialog open={manage} onOpenChange={setManage}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto rounded-[28px] p-6 sm:p-8" data-testid="manage-plan-dialog">
          <DialogTitle className="font-display text-2xl text-ww-navy">Manage Plan</DialogTitle>
          <DialogDescription>Plans are annual, one-time payments. Existing records always stay accessible.</DialogDescription>
          <PricingCards onPaid={() => setManage(false)} />
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function Notifications() {
  const { me, setMe } = useApp();
  const api = useApi();
  const toggle = async (k, v) => { try { setMe(await api("patch", "/me", { [k]: v })); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Notification Preferences" testid="account-notifications-panel">
      <Row label="In-app reminders"><Switch checked={me.notify_in_app} onCheckedChange={(v) => toggle("notify_in_app", v)} data-testid="pref-in-app-switch" /></Row>
      <Row label={me.email_verified ? `Email reminders to ${me.email}` : "Email reminders (requires a verified email)"}><Switch checked={me.notify_email} disabled={!me.email_verified} onCheckedChange={(v) => toggle("notify_email", v)} data-testid="pref-email-switch" /></Row>
      <Row label="Monthly coverage digest (email on the 1st, listing coverage ending in the next 90 days)"><Switch checked={me.notify_digest !== false} disabled={!me.email_verified || !me.notify_email} onCheckedChange={(v) => toggle("notify_digest", v)} data-testid="pref-digest-switch" /></Row>
      <p className="mt-6 rounded-2xl bg-ww-mint/60 p-4 text-sm text-ww-navy/80">Reminder schedule for every coverage period: <strong>30 days, 7 days, 1 day and expiry day</strong>, delivered in your timezone ({me.timezone}).</p>
    </Panel>
  );
}

function Payments() {
  const api = useApi();
  const [rows, setRows] = useState(null);
  useEffect(() => { api("get", "/billing/payments").then(setRows).catch(() => setRows([])); }, [api]);
  return (
    <Panel title="Payment History" testid="account-payments-panel">
      {rows?.length === 0 && <p className="text-sm text-ww-muted" data-testid="payments-empty">No payments yet.</p>}
      <div className="space-y-3">
        {rows?.map((r) => (
          <div key={r.id} className="grid gap-2 rounded-2xl border border-ww-border p-4 text-sm sm:grid-cols-6 sm:items-center" data-testid={`payment-row-${r.id}`}>
            <span>{fmtDate((r.paid_at || r.created_at).slice(0, 10))}</span><span className="capitalize">{r.plan}</span><span className="font-semibold">{r.amount_display}</span><span>{r.currency}</span>
            <span className="capitalize">{r.status}</span>
            <span className="flex items-center justify-between gap-2 truncate text-xs text-ww-muted">{r.provider_payment_id || "—"}
              {["paid", "refunded"].includes(r.status) && <button onClick={() => downloadFile(api, `/billing/payments/${r.id}/receipt`, `receipt-${r.receipt_no}.html`)} className="text-ww-emerald" aria-label="Download receipt" data-testid={`receipt-download-${r.id}`}><FileDown className="h-4 w-4" /></button>}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Privacy() {
  const api = useApi();
  const { signOut } = useAuth();
  const { me } = useApp();
  const [del, setDel] = useState(false);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const byEmail = !!(me.email && me.email_verified);
  const request = () => run(async () => { await api("post", "/me/delete/request"); setSent(true); toast.success(`Code sent to ${me.email}`); });
  const confirm = () => run(async () => { await api("post", "/me/delete/confirm", { code }); toast.success("Your account has been deleted"); await signOut({ redirectUrl: "/" }); });
  const confirmByPhone = () => run(async () => { await api("post", "/me/delete/confirm-recent"); toast.success("Your account has been deleted"); await signOut({ redirectUrl: "/" }); });
  const logoutAll = () => run(async () => { await api("post", "/auth/logout-everywhere"); await signOut({ redirectUrl: "/" }); });
  return (
    <Panel title="Privacy Controls" testid="account-privacy-panel">
      <div className="grid gap-3 sm:grid-cols-2">
        <button className="ww-btn-ghost justify-center" onClick={() => downloadFile(api, "/me/export", "warranty-wallet-export.json").catch(() => toast.error("Export failed"))} data-testid="export-data-button"><Download className="h-4 w-4" />Export Account Data</button>
        <button className="ww-btn-ghost justify-center" onClick={() => downloadFile(api, "/me/documents.zip", "warranty-wallet-documents.zip").catch(() => toast.error("No documents to download"))} data-testid="download-documents-button"><FileDown className="h-4 w-4" />Download Documents</button>
        <button className="ww-btn-ghost justify-center" onClick={() => signOut({ redirectUrl: "/" })} data-testid="logout-button"><LogOut className="h-4 w-4" />Logout</button>
        <button className="ww-btn-ghost justify-center" disabled={busy} onClick={logoutAll} data-testid="logout-everywhere-button"><LogOut className="h-4 w-4" />Sign out of all devices</button>
        <button className="ww-btn-ghost justify-center text-red-600 hover:bg-red-50" onClick={() => setDel(true)} data-testid="delete-account-button"><Trash2 className="h-4 w-4" />Delete Account</button>
      </div>
      <Dialog open={del} onOpenChange={(o) => { setDel(o); if (!o) { setSent(false); setCode(""); } }}>
        <DialogContent className="max-w-md rounded-[24px] p-7" data-testid="delete-account-dialog">
          <DialogTitle className="font-display text-xl text-ww-navy">Delete your account?</DialogTitle>
          <DialogDescription>This permanently deletes your products, documents, coverage records and reminders. Payment records are retained only where required for accounting. To confirm it's you, {byEmail ? "we'll email a 6-digit code to your verified address." : "please verify your mobile number with a one-time code."}</DialogDescription>
          {!byEmail ? <PhoneAuth reauth onDone={confirmByPhone} /> : !sent ? <button className="ww-btn-primary justify-center bg-red-600 hover:bg-red-700" disabled={busy} onClick={request} data-testid="delete-send-code-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me a confirmation code"}</button> : (
            <div className="space-y-4">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="delete-code-input"><InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup></InputOTP>
              <button className="ww-btn-primary w-full justify-center bg-red-600 hover:bg-red-700" disabled={busy || code.length !== 6} onClick={confirm} data-testid="delete-confirm-button">Permanently delete account</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

export default function Account() {
  const [params, setParams] = useSearchParams();
  const { me } = useApp();
  const tab = params.get("tab") || "profile";
  return (
    <main className="mx-auto max-w-4xl px-5 py-12 lg:px-8" data-testid="account-page">
      <h1 className="font-display text-4xl font-semibold tracking-tight text-ww-navy sm:text-5xl">Account</h1>
      <p className="mt-2 text-ww-muted">{me.name || me.email} · {countryName(me.country)}</p>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="mt-8">
        <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-2xl bg-ww-mint/60 p-1.5">
          {[["profile", "Profile"], ["subscription", "Subscription"], ["notifications", "Notifications"], ["family", "Family"], ["payments", "Payments"], ["privacy", "Privacy"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-ww-emerald" data-testid={`account-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="profile" className="mt-6"><Profile /></TabsContent>
        <TabsContent value="subscription" className="mt-6"><Subscription /></TabsContent>
        <TabsContent value="notifications" className="mt-6"><Notifications /></TabsContent>
        <TabsContent value="family" className="mt-6"><FamilySharing /></TabsContent>
        <TabsContent value="payments" className="mt-6"><Payments /></TabsContent>
        <TabsContent value="privacy" className="mt-6"><Privacy /></TabsContent>
      </Tabs>
    </main>
  );
}
